import React, { useEffect, useState } from 'react';
import { Modal, Button, LoadingSpinner } from '../../design-system';
import { ValueDiff, ValueDisplay } from '../diff/ValueDiff';

const T = {
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  ink: '#2E2109',
  ink2: '#4A3510',
  inkMuted: '#6E5A38',
  inkFaint: '#A39068',
  orange: '#EA7C1F',
  orangeDark: '#C25F0E',
  capRed: '#C14A33',
  moss: '#6B7A3E',
};

const CASCADE_TYPE_LABEL = {
  feature: 'Idea feature',
  assumption: 'Assumption',
  story: 'Plan story',
  task: 'Plan task',
  event_model: 'Event model',
  roadmap: 'Roadmap item',
};

const SEVERITY_COLOR = {
  required: T.capRed,
  recommended: T.orangeDark,
  optional: T.inkMuted,
};

const OP_LABEL = { set: 'Set', append: 'Add to', remove: 'Remove from' };
const OP_COLOR = { set: T.orangeDark, append: T.moss, remove: T.capRed };

function SectionAccent({ label, color, dot }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginTop: 18, marginBottom: 10,
    }}>
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block',
        }} />
      )}
      <div style={{
        fontFamily: T.mono, fontSize: 11, fontWeight: 800,
        letterSpacing: '0.6px', textTransform: 'uppercase',
        color,
      }}>
        {label}
      </div>
    </div>
  );
}

function ChangeRow({ change }) {
  const { path, operation, value, section_label, target_label, field, is_noop } = change;
  const opKey = (operation || '').toLowerCase();
  const opLabel = OP_LABEL[opKey] || operation;
  const opColor = OP_COLOR[opKey] || T.orangeDark;

  const labelParts = [section_label, target_label, field].filter(Boolean);
  const subject = labelParts.length > 0 ? labelParts.join(' · ') : (path || '');

  return (
    <div style={{
      padding: 12, borderRadius: 8,
      background: is_noop ? 'rgba(214,196,154,0.18)' : 'rgba(234,124,31,0.10)',
      border: `1px ${is_noop ? 'dashed' : 'solid'} ${is_noop ? 'rgba(191,171,121,0.45)' : 'rgba(234,124,31,0.30)'}`,
      marginBottom: 8,
      opacity: is_noop ? 0.78 : 1,
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: T.mono, fontSize: 10, fontWeight: 800,
          letterSpacing: '0.6px', textTransform: 'uppercase',
          color: is_noop ? T.inkFaint : opColor,
          background: 'rgba(255,255,255,0.55)',
          padding: '2px 8px', borderRadius: 999,
          border: `1px solid ${is_noop ? 'rgba(163,144,104,0.4)' : opColor + '33'}`,
        }}>
          {opLabel}
        </span>
        <span style={{
          fontFamily: T.sans, fontSize: 14, fontWeight: 700,
          color: is_noop ? T.inkMuted : T.ink,
        }}>
          {subject}
        </span>
        {is_noop && (
          <span style={{
            fontFamily: T.hand, fontSize: 14, color: T.inkFaint,
            marginLeft: 'auto',
          }}>
            already matches — no change
          </span>
        )}
      </div>
      {opKey !== 'remove' && value !== undefined && value !== null && !is_noop && (
        <div style={{ marginTop: 8 }}>
          <ValueDisplay value={value} />
        </div>
      )}
    </div>
  );
}

function CascadeRow({ cascade, checked, onToggle }) {
  const type = cascade.cascade_type || cascade.type || 'unknown';
  const sevColor = SEVERITY_COLOR[cascade.severity] || T.orangeDark;
  const reason = cascade.reason || cascade.summary || cascade.description || '';
  const target = cascade.target_name || cascade.target_id || '';
  const field = cascade.field;
  const oldVal = cascade.old_value;
  const newVal = cascade.new_value;

  return (
    <label style={{
      display: 'block',
      padding: 12, borderRadius: 8,
      background: checked ? 'rgba(140,158,86,0.16)' : 'rgba(140,158,86,0.06)',
      border: `1px solid ${checked ? 'rgba(107,122,62,0.32)' : 'rgba(107,122,62,0.18)'}`,
      marginBottom: 8,
      cursor: 'pointer',
      opacity: checked ? 1 : 0.78,
      transition: 'background 120ms ease, opacity 120ms ease',
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          style={{ marginTop: 4, accentColor: T.moss }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              fontFamily: T.mono, fontSize: 10, fontWeight: 800,
              letterSpacing: '0.6px', textTransform: 'uppercase',
              color: '#506024',
              background: 'rgba(255,255,255,0.55)',
              padding: '2px 8px', borderRadius: 999,
              border: '1px solid rgba(80,96,36,0.25)',
            }}>
              {CASCADE_TYPE_LABEL[type] || type}
              {field ? ` · ${field}` : ''}
            </span>
            {target && (
              <span style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 700, color: T.ink }}>
                {target}
              </span>
            )}
            {cascade.severity && (
              <span style={{
                fontFamily: T.mono, fontSize: 10, fontWeight: 700,
                letterSpacing: '0.5px', textTransform: 'uppercase',
                color: sevColor,
              }}>
                · {cascade.severity}
              </span>
            )}
          </div>

          {(oldVal !== undefined && oldVal !== null) || (newVal !== undefined && newVal !== null) ? (
            <div style={{ marginBottom: 6 }}>
              <ValueDiff oldValue={oldVal} newValue={newVal} />
            </div>
          ) : null}

          {reason && (
            <div style={{
              marginTop: 4,
              paddingLeft: 12,
              borderLeft: '3px solid rgba(107,122,62,0.45)',
            }}>
              <div style={{
                fontFamily: T.mono, fontSize: 10, fontWeight: 800,
                letterSpacing: '0.6px', textTransform: 'uppercase',
                color: '#506024', marginBottom: 4,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  fontFamily: T.sans, fontSize: 12,
                  fontWeight: 800, color: '#506024',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 14, height: 14, borderRadius: '50%',
                  background: 'rgba(140,158,86,0.30)',
                  border: '1px solid rgba(107,122,62,0.45)',
                  lineHeight: 1,
                }}>?</span>
                Why this cascade is suggested
              </div>
              <div style={{
                fontFamily: T.sans, fontSize: 13, color: T.ink2,
                lineHeight: 1.55, fontStyle: 'italic',
              }}>
                {reason}
              </div>
            </div>
          )}
        </div>
      </div>
    </label>
  );
}

/**
 * Two-step preview/apply modal. Opens with `mode: 'preview'`, displays the
 * spec changes + cascade checkboxes, and the user clicks Apply to commit.
 *
 * Props:
 *   preview: the response from /preview-update-spec (or null while loading)
 *   loading: bool — preview is being fetched
 *   applying: bool — apply is in flight
 *   error: any error from the preview call
 *   onApply(approvedCascades): user clicked Apply
 *   onClose(): user dismissed
 */
export default function UpdateSpecResultModal({
  preview,
  loading,
  applying,
  error,
  onApply,
  onClose,
  onDismiss,
  dismissing,
}) {
  const [approved, setApproved] = useState(() => new Set());

  // Default-approve all cascades when a fresh preview arrives.
  useEffect(() => {
    if (preview?.cascade_preview) {
      setApproved(new Set(preview.cascade_preview.map((_, i) => i)));
    } else {
      setApproved(new Set());
    }
  }, [preview]);

  const isOpen = !!(loading || preview || error);
  if (!isOpen) return null;

  const cascades = preview?.cascade_preview || [];
  const applied_changes = preview?.applied_changes || [];
  const cascade_warnings = preview?.cascade_warnings || [];
  const cascade_filter_notes = preview?.cascade_filter_notes || [];
  const finding_title = preview?.finding_title;
  const finding_id = preview?.finding_id;
  const is_stale = !!preview?.is_stale;
  const approvedCount = approved.size;

  const toggleCascade = (idx) => {
    setApproved((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleApply = () => {
    if (!preview || !onApply) return;
    const approvedCascades = cascades.filter((_, i) => approved.has(i));
    onApply(approvedCascades);
  };

  return (
    <Modal isOpen onClose={onClose} title="Review changes before applying" size="lg">
      <div style={{ fontFamily: T.sans, color: T.ink2 }}>
        {loading ? (
          <div style={{
            padding: '32px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 18,
            background: 'rgba(214,196,154,0.22)',
            border: '1px solid rgba(191,171,121,0.45)',
            borderRadius: 8,
          }}>
            <LoadingSpinner />
            <div style={{ textAlign: 'center', maxWidth: 420 }}>
              <div style={{
                fontFamily: T.sans, fontSize: 16, fontWeight: 700,
                color: T.ink, marginBottom: 6,
              }}>
                Analyzing what would change
              </div>
              <div style={{
                fontFamily: T.sans, fontSize: 13.5, color: T.ink2,
                lineHeight: 1.55,
              }}>
                We're applying this finding to your spec in memory, then asking
                the model what stories, features, assumptions, and event-model
                elements may need to be updated downstream. Nothing has been
                saved yet — you'll get to review and approve before anything
                changes.
              </div>
              <div style={{
                fontFamily: T.hand, fontSize: 16, color: T.inkFaint, marginTop: 10,
              }}>
                this can take a few seconds —
              </div>
            </div>
          </div>
        ) : error ? (
          <div style={{
            padding: 12, borderRadius: 8,
            background: 'rgba(193,74,51,0.10)',
            border: '1px solid rgba(193,74,51,0.28)',
            color: T.capRed,
          }}>
            Could not load preview: {error?.response?.data?.detail || error?.message || 'unknown error'}
          </div>
        ) : (
          <>
            {finding_title && (
              <div style={{
                padding: 12, borderRadius: 8,
                background: 'rgba(214,196,154,0.32)',
                border: '1px solid rgba(191,171,121,0.55)',
                marginBottom: 4,
              }}>
                <div style={{ fontSize: 14, color: T.ink, fontWeight: 600 }}>
                  {finding_title}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: T.inkMuted }}>
                  Nothing has been changed yet. Review and confirm below.
                </div>
              </div>
            )}

            {is_stale && (
              <div style={{
                marginTop: 12, padding: 14, borderRadius: 8,
                background: 'rgba(143,180,194,0.16)',
                border: '1px solid rgba(143,180,194,0.45)',
              }}>
                <div style={{
                  fontFamily: T.sans, fontSize: 14, fontWeight: 700,
                  color: T.ink, marginBottom: 4,
                }}>
                  This finding looks stale.
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
                  The spec already matches what this finding proposes — applying it
                  wouldn't change anything. Either dismiss it now, or rescan to
                  refresh the inbox.
                </div>
                {onDismiss && (
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        if (finding_id) onDismiss(finding_id);
                      }}
                      disabled={dismissing}
                    >
                      {dismissing ? 'Dismissing…' : 'Dismiss this finding'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <SectionAccent label="Changes to the spec" color={T.orangeDark} dot />
            {applied_changes.length === 0 ? (
              <div style={{ fontSize: 13, color: T.inkMuted, fontStyle: 'italic' }}>
                No structural changes — this finding has no proposed_spec_diff.
              </div>
            ) : (
              applied_changes.map((c, i) => <ChangeRow key={i} change={c} />)
            )}

            <SectionAccent
              label={`Downstream cascades (${approvedCount}/${cascades.length} selected)`}
              color={T.moss}
              dot
            />
            {cascades.length === 0 ? (
              <div style={{
                padding: 12, borderRadius: 8,
                background: 'rgba(140,158,86,0.10)',
                border: '1px dashed rgba(107,122,62,0.32)',
                fontSize: 13, color: T.inkMuted,
              }}>
                No cascade suggestions — downstream artifacts likely don't need updating.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: T.inkMuted, marginBottom: 10 }}>
                  Uncheck any cascade you don't want applied. The spec change above is always applied.
                </div>
                {cascades.map((c, i) => (
                  <CascadeRow
                    key={i}
                    cascade={c}
                    checked={approved.has(i)}
                    onToggle={() => toggleCascade(i)}
                  />
                ))}
              </>
            )}

            {cascade_warnings.length > 0 && (
              <>
                <SectionAccent label="Warnings" color={T.capRed} dot />
                {cascade_warnings.map((w, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', borderRadius: 6,
                    background: 'rgba(193,74,51,0.10)',
                    border: '1px solid rgba(193,74,51,0.28)',
                    fontSize: 13, color: T.capRed, marginBottom: 6,
                  }}>
                    {w}
                  </div>
                ))}
              </>
            )}

            {cascade_filter_notes.length > 0 && (
              <>
                <SectionAccent label="Filtered out" color={T.inkFaint} dot />
                <div style={{ fontSize: 12, color: T.inkMuted, marginBottom: 8 }}>
                  We dropped {cascade_filter_notes.length} suggestion{cascade_filter_notes.length === 1 ? '' : 's'} that looked like noise. Hover for details.
                </div>
                {cascade_filter_notes.map((note, i) => (
                  <div key={i} style={{
                    padding: '6px 10px', borderRadius: 6,
                    background: 'rgba(214,196,154,0.18)',
                    border: '1px dashed rgba(191,171,121,0.45)',
                    fontFamily: T.sans, fontSize: 12.5, color: T.inkMuted,
                    marginBottom: 4, lineHeight: 1.5,
                  }}>
                    {note}
                  </div>
                ))}
              </>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 12, marginTop: 22,
            }}>
              <Button variant="secondary" onClick={onClose} disabled={applying}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApply} disabled={applying}>
                {applying
                  ? 'Applying…'
                  : `Apply${approvedCount > 0 ? ` + ${approvedCount} cascade${approvedCount === 1 ? '' : 's'}` : ''}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
